'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';

interface LensHeroProps {
  onUseLens?: () => void;
}

export default function LensHero({ onUseLens }: LensHeroProps) {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };
    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const names = ['AR-RAHMAN', 'AR-RAZZAQ', 'AL-HASIB', 'AL-ALIM', 'AL-BARI', 'AL-BASIR', 'AL-HAKIM', 'AL-NUR', 'AL-MALIK', 'AL-MUHAYMIN', 'AL-HAFIZ', 'AL-ADL', 'AL-HAQQ', 'AL-AHAD', 'AL-QADIR', 'AL-KHALIQ', 'AL-KHABIR', 'AL-ZAHIR'];

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay pt-20">
      {/* Cursor glow */}
      <div
        ref={cursorRef}
        style={{
          position: 'fixed',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 65%)',
          pointerEvents: 'none',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          willChange: 'transform',
        }}
      />
      <div className="pixel-border-gold-top" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
      {/* Names of Allah ring */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="-300 -300 600 600" width="700" height="700">
          {names?.map((name, i) => {
            const angle = (i / names?.length) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const r = 200;
            const x = r * Math.cos(rad);
            const y = r * Math.sin(rad);
            return (
              <text key={name} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                fill="rgba(179,138,42,0.8)" fontSize="7" fontFamily="monospace" opacity="0.4" letterSpacing="1"
                transform={`rotate(${angle + 90}, ${x}, ${y})`}
              >
                {name}
              </text>
            );
          })}
          {[80, 140, 200, 260]?.map((r) => (
            <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(139,105,20,0.15)" strokeWidth="0.5" opacity="1" />
          ))}
          <circle cx="0" cy="0" r="80" fill="rgba(28,43,37,0.6)" />
          <circle cx="0" cy="0" r="70" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1" opacity="0.95" />
          <text x="0" y="-8" textAnchor="middle" fill="#F5F0E8" fontSize="12" fontFamily="monospace" fontWeight="900" opacity="0.9">LENS</text>
          <text x="0" y="10" textAnchor="middle" fill="rgba(179,138,42,0.7)" fontSize="6" fontFamily="monospace" opacity="1" letterSpacing="2">INNER REALITY</text>
        </svg>
      </div>
      <div className={`relative z-10 max-w-[900px] mx-auto px-6 text-center transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{ color: 'rgba(139,105,20,0.7)' }}>05 — Application Tool</p>
        <h1 className="font-mono text-5xl md:text-7xl font-black uppercase tracking-tight leading-none mb-6" style={{ color: '#F5F0E8' }}>
          INNER<br />
          <span className="text-shimmer">REALITY LENS</span>
        </h1>
        {/* Recognition arc tagline */}
        <div className="mb-4 flex items-center justify-center gap-3">
          <span style={{ width: 32, height: 1, background: 'rgba(179,138,42,0.4)', display: 'inline-block' }} />
          <p className="font-mono text-xs tracking-widest uppercase" style={{ color: 'rgba(179,138,42,0.75)' }}>
            Go from <span style={{ color: 'rgba(179,138,42,1)' }}>Aware</span> to <span style={{ color: '#F5F0E8' }}>Conscious</span>
          </p>
          <span style={{ width: 32, height: 1, background: 'rgba(179,138,42,0.4)', display: 'inline-block' }} />
        </div>
        {/* Recognition arc stages */}
        <div className="mb-8 flex items-center justify-center flex-wrap gap-1" style={{ opacity: 0.55 }}>
          {['Mata', 'Encounter', 'Examination', 'Recognition', 'Re-Assembly', 'Uboor'].map((stage, i, arr) => (
            <React.Fragment key={stage}>
              <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(245,240,232,0.6)' }}>{stage}</span>
              {i < arr.length - 1 && <span className="font-mono text-[9px]" style={{ color: 'rgba(179,138,42,0.4)' }}>→</span>}
            </React.Fragment>
          ))}
        </div>
        <p className="font-mono text-sm max-w-lg mx-auto leading-relaxed mb-12" style={{ color: 'rgba(245,240,232,0.5)' }}>
          A tool for recognizing things in creation as Ayat. The lens is the methodology — perceiving immediate surroundings as composed of signs pointing back to the Singular cause.
        </p>

        {/* Foundational verse — Quran 41:53 */}
        <div className="max-w-lg mx-auto mb-10 p-5 rounded-lg" style={{ background: 'rgba(139,105,20,0.07)', border: '1px solid rgba(179,138,42,0.2)' }}>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#D4A82A', direction: 'rtl', textAlign: 'right', lineHeight: 1.8, marginBottom: 8 }}>
            سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ حَتَّىٰ يَتَبَيَّنَ لَهُمْ أَنَّهُ الْحَقُّ
          </p>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, fontStyle: 'italic', marginBottom: 6 }}>
            "Soon We will show them Our signs in the horizons and in themselves until it becomes clear that it is the Truth."
          </p>
          <a
            href="https://quran.com/41/53"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] tracking-widest"
            style={{ color: 'rgba(179,138,42,0.5)' }}
          >
            Quran 41:53 ↗
          </a>
        </div>

        <div className="flex items-center justify-center gap-6">
          <button
            className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors cursor-pointer"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
            onClick={() => {
              if (onUseLens) {
                onUseLens();
              } else {
                document.getElementById('lens-tool')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Use the Lens
          </button>
          <Link href="/muslim-resource-guide" className="font-mono text-xs tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.4)', borderColor: 'rgba(139,105,20,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
          >
            Next: Muslim Resource Guide →
          </Link>
        </div>
      </div>
    </section>
  );
}
